import type { ChatbotMode } from '@/components-restructure/ChatbotPage/useChatbotMode';
import cuid from 'cuid';
import { z } from 'zod';
import { createStore, useStore } from 'zustand';
import type { TrackFn } from '../analytics/useAnalytics';
import { createZustandContext } from '../zustand';

// Version type for history management
export type VersionType = {
  id: string;
  createdAt: Date;
  versionNumber: number;
  name: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: API response type may vary
  contents: any;
  noteId: string;
  createdBy: string | null;
  isReversion: boolean;
  _timestamp?: number;
  _selectionTimestamp?: number;
};

export const getNoteSessionId = () => {
  const storedId = sessionStorage.getItem('editor-session-id');
  const storedTimestamp = sessionStorage.getItem('editor-session-created-at');

  if (storedId && storedTimestamp && typeof storedId === 'string') {
    const createdAt = new Date(storedTimestamp);
    const now = new Date();
    const timeDiffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);

    if (timeDiffMinutes < 10) {
      return storedId;
    }
  }

  const newId = cuid();
  const now = new Date().toISOString();
  sessionStorage.setItem('editor-session-id', newId);
  sessionStorage.setItem('editor-session-created-at', now);
  return newId;
};

export interface ChatState {
  active: SidebarType[];
  setAll: (value: SidebarType[]) => void;
  toggle: (sidebar: SidebarType, value: boolean) => void;
  collapseRightSidebar: () => void;

  mode: ChatbotMode;

  setMode: (mode: ChatState['mode']) => void;
  // Version history state
  selectedVersion: VersionType | null;
  isPreviewingVersion: boolean;
  isHistorySidebarOpen: boolean;

  // Version history actions
  selectVersion: (version: VersionType) => void;
  closeVersionPreview: () => void;
  toggleHistorySidebar: () => void;
  resetVersionHistory: () => void;

  viewType: 'table' | 'tabs';
  setViewType: (viewType: 'table' | 'tabs') => void;
}

// Define all possible sidebar types
export type SidebarType =
  | 'metadata'
  | 'notes'
  | 'chat'
  | 'metadata.related'
  | 'metadata.annotations'
  | 'note-history';
const zodSidebarType = z.enum([
  'metadata',
  'notes',
  'chat',
  'note-history',
  'metadata.related',
  'metadata.annotations',
]);

const resolveSidebarConflict = (sidebars: SidebarType[]): SidebarType[] => {
  const hasMetadata = sidebars.includes('metadata');
  const hasNotes = sidebars.includes('notes');

  if (hasMetadata && hasNotes) {
    // Find the last occurrence of either 'metadata' or 'notes'
    const lastIndex = Math.max(
      sidebars.lastIndexOf('metadata'),
      sidebars.lastIndexOf('notes'),
    );
    const keepSidebar = sidebars[lastIndex];

    // Filter out both and then add back the one we want to keep
    return [
      ...sidebars.filter((s) => s !== 'metadata' && s !== 'notes'),
      keepSidebar,
    ];
  }

  return sidebars;
};

const trackChangedSidebars = (
  track: TrackFn,
  active: SidebarType[],
  oldSidebars: SidebarType[],
) => {
  const openedSidebars = active.filter(
    (sidebar) => !oldSidebars.includes(sidebar),
  );

  const closedSidebars = oldSidebars.filter(
    (sidebar) => !active.includes(sidebar),
  );

  for (const sidebar of openedSidebars) {
    track('sidebar_open', { sidebar });
  }

  for (const sidebar of closedSidebars) {
    track('sidebar_closed', { sidebar });
  }
};

const ChatbotStore = createZustandContext(
  ({
    active,
    track,
    mode,
  }: Pick<ChatState, 'active' | 'mode'> & {
    track: TrackFn;
  }) => {
    return createStore<ChatState>((set, get) => ({
      active,

      mode,

      setMode: (newMode) => {
        set({ mode: newMode });
      },

      setAll: (newSidebars) => {
        const parsedSidebars = newSidebars.map((s) => zodSidebarType.parse(s));
        const resolvedSidebars = resolveSidebarConflict(parsedSidebars);

        const oldSidebars = get().active;

        trackChangedSidebars(track, resolvedSidebars, oldSidebars);

        set({ active: resolvedSidebars });
      },

      viewType: 'table',
      setViewType: (viewType) => {
        set({ viewType });
      },

      toggle: (sidebar, value) => {
        const sidebars = get().active;

        track(value ? 'sidebar_open' : 'sidebar_closed', {
          sidebar,
        });

        if (value) {
          const newSidebars = [...sidebars, sidebar];
          set({ active: resolveSidebarConflict(newSidebars) });

          if (sidebar === 'note-history') {
            set({ isHistorySidebarOpen: true });
          }
        } else {
          set({ active: sidebars.filter((s) => s !== sidebar) });

          if (sidebar === 'note-history') {
            get().resetVersionHistory();
            set({ isHistorySidebarOpen: false });
          }
        }
      },
      collapseRightSidebar: () => {
        const sidebars = get().active;

        const closedSidebar = sidebars.find(
          (s) => s === 'metadata' || s === 'notes',
        );

        if (closedSidebar) {
          track('sidebar_closed', { sidebar: closedSidebar });
        }
        set({
          active: sidebars.filter((s) => s !== 'metadata' && s !== 'notes'),
        });
      },

      selectedVersion: null,
      isPreviewingVersion: false,
      isHistorySidebarOpen: false,

      selectVersion: (version) => {
        if (!version.contents) {
          console.error('Version missing contents:', version);
          return;
        }

        const timestamp = Date.now();
        const versionWithUniqueId = {
          ...version,
          _timestamp: timestamp,
        };

        set({
          selectedVersion: versionWithUniqueId,
          isPreviewingVersion: true,
        });
      },
      closeVersionPreview: () => {
        set({
          selectedVersion: null,
          isPreviewingVersion: false,
        });
      },
      toggleHistorySidebar: () => {
        const currentValue = get().isHistorySidebarOpen;
        const newValue = !currentValue;
        set({ isHistorySidebarOpen: newValue });

        const toggle = get().toggle;
        toggle('note-history', newValue);

        if (!newValue) {
          get().resetVersionHistory();
        }
      },
      resetVersionHistory: () => {
        set({
          selectedVersion: null,
          isPreviewingVersion: false,
        });
      },
    }));
  },
);

export const useChatbotStore = <T,>(selector: (state: ChatState) => T) =>
  useStore(ChatbotStore.useContext(), selector);

export const getDefaults = (mode: ChatbotMode | undefined): SidebarType[] => {
  if (mode?.type !== 'chatbot') return ['chat'];

  const chatbotType = mode?.chatbotType;

  if (chatbotType === 'NOTE') return [];

  return ['chat'];
};

export default ChatbotStore;
